import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Select, DatePicker, Spin, Alert } from "antd";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { ROLL_CALL_API_BASE_URL } from "../../RollCall/RollCallConfig/RollCallConfig";
import {
  pctFromString,
  bandFor,
  BAND_COLORS,
  fmtNum,
  getMonthRange,
  getPreviousMonth,
  monthLabel,
  adaptBranchPerformanceResponse,
} from "../../../../feature-module/utils/kpiReportUtils";

export default function KpiPerformanceReport() {
  const navigate = useNavigate();

  const [tokenData, setTokenData] = useState(null);
  const [superIdData, setSuperIdData] = useState(null);

  const [branchdata, setbranchdata] = useState([]);
  const [branchId, setBranchId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(dayjs("2026-06-01"));

  const [current, setCurrent] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    const superId = sessionStorage.getItem("superId");

    if (token && superId) {
      setTokenData(token);
      setSuperIdData(superId);
    } else {
      navigate("/signin");
    }
  }, [navigate]);

  useEffect(() => {
    if (!tokenData || !superIdData) return;

    const fetchbranches = async () => {
      try {
        const response = await fetch(
          `${ROLL_CALL_API_BASE_URL}portal/branch/getbranch?superid=${superIdData}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokenData}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error("Failed to fetch branches");
        }

        const data = await response.json();

        if (data.status) {
          const list = data.ResultData || [];
          setbranchdata(list);
          if (list.length > 0) {
            setBranchId((prev) => prev ?? list[0].branchid);
          }
        } else {
          console.error("Invalid branch response structure:", data);
        }
      } catch (err) {
        console.error("Error fetching branches:", err.message);
      }
    };

    fetchbranches();
  }, [superIdData, tokenData]);

  useEffect(() => {
    if (!tokenData || branchId == null) return;

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const prevMonth = getPreviousMonth(selectedMonth);
        const curRange = getMonthRange(selectedMonth);
        const prevRange = getMonthRange(prevMonth);

        const fetchReport = async (startDate, endDate) => {
          const url = `${ROLL_CALL_API_BASE_URL}portal/kpi/BranchPerformanceReport?BranchId=${branchId}&StartDate=${startDate}&EndDate=${endDate}`;
          const res = await fetch(url, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokenData}`,
            },
          });
          if (!res.ok) {
            throw new Error(
              `BranchPerformanceReport request failed (${res.status})`,
            );
          }
          return res.json();
        };

        const [curRes, prevRes] = await Promise.all([
          fetchReport(curRange.StartDate, curRange.EndDate),
          fetchReport(prevRange.StartDate, prevRange.EndDate),
        ]);

        if (cancelled) return;

        setCurrent(
          adaptBranchPerformanceResponse(curRes, monthLabel(selectedMonth)),
        );
        setPrevious(
          adaptBranchPerformanceResponse(prevRes, monthLabel(prevMonth)),
        );
      } catch (err) {
        if (!cancelled)
          setError(err.message || "Failed to load performance report");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [branchId, selectedMonth, tokenData]);

  const branchOptions = [
    ...(branchdata?.map((item) => ({
      value: item.BranchId || item.branchid,
      label: item.Name,
    })) ?? []),
  ];

  return (
    <div className="page-wrapper">
      <div className="container-fluid py-3">
        <div className="d-flex flex-wrap justify-content-end gap-2 mb-3">
          <Select
            value={branchId}
            onChange={setBranchId}
            style={{ width: 200 }}
            options={branchOptions}
            placeholder="Select branch"
            loading={!branchdata.length}
          />
          <DatePicker
            picker="month"
            value={selectedMonth}
            onChange={(val) => val && setSelectedMonth(val)}
            allowClear={false}
            style={{ width: 160 }}
          />
        </div>

        {error && (
          <Alert type="error" message={error} showIcon className="mb-3" />
        )}

        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spin size="large" />
          </div>
        ) : current ? (
          <>
            <BranchHeaderCard current={current} previous={previous} />
            <ComparisonTable current={current} previous={previous} />
            <Legend />
          </>
        ) : (
          <Alert
            type="info"
            message="No data found for this branch and month."
            showIcon
          />
        )}
      </div>
    </div>
  );
}

function BranchHeaderCard({ current, previous }) {
  const currentBand = bandFor(current.TotalScore);
  const hasPrevious = Boolean(previous);
  const delta = hasPrevious ? current.TotalScore - previous.TotalScore : 0;
  const initials = (current.BranchName || "NA").slice(0, 2).toUpperCase();

  const deltaBg = delta < 0 ? "#fff1f2" : delta > 0 ? "#ecfdf5" : "#f8fafc";
  const deltaBorder = delta < 0 ? "#fecdd3" : delta > 0 ? "#a7f3d0" : "#e2e8f0";
  const deltaPillBg = delta < 0 ? "#f43f5e" : delta > 0 ? "#10b981" : "#94a3b8";
  const deltaText = delta < 0 ? "#be123c" : delta > 0 ? "#047857" : "#475569";
  const deltaIcon =
    delta < 0
      ? "fa-arrow-trend-down"
      : delta > 0
        ? "fa-arrow-trend-up"
        : "fa-minus";

  return (
    <div className="card mb-3">
      <div
        className="d-flex justify-content-between align-items-center flex-wrap gap-3 p-3"
        style={{
          backgroundColor: "#475569",
          color: "#fff",
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        }}
      >
        <div className="d-flex align-items-center gap-3">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center fw-semibold"
            style={{
              width: 56,
              height: 56,
              backgroundColor: "rgba(255,255,255,0.15)",
              fontSize: 18,
              flexShrink: 0,
            }}
          >
            {initials}
          </div>
          <div>
            <div
              className="fw-semibold"
              style={{ fontSize: 18, lineHeight: 1.2 }}
            >
              {current.BranchName}
            </div>
            <div style={{ color: "#cbd5e1", fontSize: 13 }}>KPI Scorecard</div>
          </div>
        </div>
        <div className="d-flex align-items-center gap-3">
          <div style={{ color: "#cbd5e1", fontSize: 13, textAlign: "right" }}>
            {current.Month}
          </div>
          <ScoreGauge score={current.TotalScore} band={currentBand} />
        </div>
      </div>

      {hasPrevious && (
        <div
          className="d-flex align-items-start gap-2 p-3"
          style={{
            backgroundColor: deltaBg,
            borderTop: `1px solid ${deltaBorder}`,
          }}
        >
          <span
            className="d-inline-flex align-items-center gap-1 fw-semibold text-white flex-shrink-0"
            style={{
              backgroundColor: deltaPillBg,
              borderRadius: 999,
              padding: "4px 10px",
              fontSize: 12,
            }}
          >
            <i className={`fa-solid ${deltaIcon}`} />
            {delta === 0 ? "0 pts" : `${delta > 0 ? "+" : ""}${delta} pts`}
          </span>
          <p className="mb-0" style={{ color: deltaText, fontSize: 14 }}>
            In <strong>{current.Month}</strong> the score{" "}
            {delta < 0 ? "dropped" : delta > 0 ? "improved" : "held steady"} vs{" "}
            <strong>{previous.Month}</strong> ({previous.TotalScore} →{" "}
            {current.TotalScore}).
          </p>
        </div>
      )}
    </div>
  );
}

BranchHeaderCard.propTypes = {
  current: PropTypes.shape({
    BranchName: PropTypes.string,
    Month: PropTypes.string,
    TotalScore: PropTypes.number,
  }).isRequired,
  previous: PropTypes.shape({
    Month: PropTypes.string,
    TotalScore: PropTypes.number,
  }),
};

BranchHeaderCard.defaultProps = {
  previous: null,
};

function ScoreGauge({ score, band }) {
  const angle = Math.min(100, Math.max(0, score)) * 3.6;
  const ringColor = BAND_COLORS[band].dot;

  return (
    <div
      className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
      style={{
        width: 64,
        height: 64,
        background: `conic-gradient(${ringColor} ${angle}deg, rgba(255,255,255,0.25) ${angle}deg)`,
      }}
    >
      <div
        className="rounded-circle d-flex flex-column align-items-center justify-content-center"
        style={{
          width: 52,
          height: 52,
          backgroundColor: "#475569",
          lineHeight: 1,
        }}
      >
        <span className="fw-bold text-white" style={{ fontSize: 14 }}>
          {score}
        </span>
        <span style={{ fontSize: 9, color: "#cbd5e1" }}>/100</span>
      </div>
    </div>
  );
}

ScoreGauge.propTypes = {
  score: PropTypes.number.isRequired,
  band: PropTypes.oneOf(["green", "orange", "red"]).isRequired,
};
function ComparisonTable({ current, previous }) {
  return (
    <div className="card mb-3">
      <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
        <h6 className="mb-0 fw-semibold">Metric Comparison</h6>
        <span className="text-muted small">
          {current.Metrics.length} metrics
        </span>
      </div>

      <div className="table-responsive">
        <table
          className="table table-bordered align-middle mb-0"
          style={{ width: "auto" }}
        >
          <thead>
            <tr>
              <th
                className="small fw-semibold px-3 py-3 border-bottom text-nowrap"
                style={{
                  position: "sticky",
                  left: 0,
                  background: "#f8fafc",
                  zIndex: 2,
                }}
              >
                Month
              </th>
              {current.Metrics.map((m) => (
                <th
                  key={m.MetricName}
                  className="small fw-semibold px-3 py-3 border-bottom text-nowrap text-center"
                >
                  {m.MetricName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <MonthRow monthData={current} compareTo={previous} highlight />
            {previous && <MonthRow monthData={previous} compareTo={current} />}
          </tbody>
        </table>
      </div>
    </div>
  );
}

ComparisonTable.propTypes = {
  current: PropTypes.shape({
    Metrics: PropTypes.array.isRequired,
  }).isRequired,
  previous: PropTypes.shape({
    Metrics: PropTypes.array,
  }),
};

ComparisonTable.defaultProps = {
  previous: null,
};

// function MonthRow({ monthData, compareTo, highlight }) {
//   const rowBg = highlight ? "#f8fafc" : "#ffffff";

//   return (
//     <tr>
//       <td
//         className="fw-semibold text-nowrap px-3 py-3 border-bottom"
//         style={{ position: "sticky", left: 0, background: rowBg, zIndex: 1 }}
//       >
//         {monthData.Month}
//         {highlight && (
//           <span className="ms-2   fw-semibold" style={{ fontSize: 12 }}></span>
//         )}
//       </td>
//       {monthData.Metrics.map((m, i) => {
//         const pct = pctFromString(m.TargetAchievement);
//         const band = bandFor(pct);
//         const colors = BAND_COLORS[band];
//         const other = compareTo ? compareTo.Metrics[i] : null;
//         const otherPct = other ? pctFromString(other.TargetAchievement) : null;
//         const trend =
//           otherPct === null
//             ? null
//             : pct > otherPct
//               ? "up"
//               : pct < otherPct
//                 ? "down"
//                 : "flat";

//         return (
//           <td
//             key={m.MetricName}
//             className="px-3 py-3 border-bottom"
//             style={{ backgroundColor: rowBg }}
//           >
//             <span
//               className="d-inline-flex align-items-center gap-1 fw-semibold"
//               style={{
//                 backgroundColor: colors.bg,
//                 color: colors.text,
//                 border: `1px solid ${colors.border}`,
//                 borderRadius: 6,
//                 padding: "4px 10px",
//                 fontSize: 12,
//               }}
//             >
//               {m.TargetAchievement}
//               {highlight && trend && trend !== "flat" && (
//                 <i
//                   className={`fa-solid ${trend === "up" ? "fa-arrow-trend-up" : "fa-arrow-trend-down"}`}
//                   style={{
//                     color: trend === "up" ? "#10b981" : "#f43f5e",
//                     fontSize: 12,
//                   }}
//                 />
//               )}
//             </span>
//             <br />
//             <div className=" mt-1 fw-bold" style={{ fontSize: 12 }}>
//               {fmtNum(m.Achieved)} / {fmtNum(m.Target)}
//             </div>
//           </td>
//         );
//       })}
//     </tr>
//   );
// }

function MonthRow({ monthData, compareTo, highlight }) {
  const rowBg = highlight ? "#f8fafc" : "#ffffff";

  return (
    <tr>
      <td
        className="fw-semibold text-nowrap px-3 py-3 border-bottom"
        style={{ position: "sticky", left: 0, background: rowBg, zIndex: 1 }}
      >
        {monthData.Month}
      </td>
      {monthData.Metrics.map((m, i) => {
        const pct = pctFromString(m.TargetAchievement);
        const band = bandFor(pct);
        const colors = BAND_COLORS[band];
        const other = compareTo ? compareTo.Metrics[i] : null;
        const otherPct = other ? pctFromString(other.TargetAchievement) : null;
        const trend =
          otherPct === null
            ? null
            : pct > otherPct
              ? "up"
              : pct < otherPct
                ? "down"
                : "flat";

        return (
          <td
            key={m.MetricName}
            className="p-0 border-bottom"
            style={{ verticalAlign: "top" }}
          >
            {/* Top: light tint, shows % achievement */}
            <div
              className="d-flex align-items-center justify-content-center gap-1 fw-semibold"
              style={{
                backgroundColor: colors.bg,
                color: colors.text,
                fontSize: 13,
                padding: "8px 10px",
              }}
            >
              {m.TargetAchievement}
              {highlight && trend && trend !== "flat" && (
                <i
                  className={`fa-solid ${trend === "up" ? "fa-arrow-trend-up" : "fa-arrow-trend-down"}`}
                  style={{ fontSize: 11, color: colors.text }}
                />
              )}
            </div>

            {/* Bottom: bright/solid band color, shows Achieved/Target */}
            <div
              className="text-center fw-bold"
              style={{
                backgroundColor: colors.dot,
                color: "#fff",
                fontSize: 12,
                padding: "6px 10px",
              }}
            >
              {fmtNum(m.Achieved)} / {fmtNum(m.Target)}
            </div>
          </td>
        );
      })}
    </tr>
  );
}
const metricShape = PropTypes.shape({
  MetricName: PropTypes.string.isRequired,
  Target: PropTypes.number.isRequired,
  Achieved: PropTypes.number.isRequired,
  TargetAchievement: PropTypes.string.isRequired,
  MetricScore: PropTypes.number,
});

const monthShape = PropTypes.shape({
  Month: PropTypes.string.isRequired,
  BranchName: PropTypes.string,
  TotalScore: PropTypes.number,
  Metrics: PropTypes.arrayOf(metricShape).isRequired,
});

MonthRow.propTypes = {
  monthData: monthShape.isRequired,
  compareTo: monthShape,
  highlight: PropTypes.bool,
};

MonthRow.defaultProps = {
  compareTo: null,
  highlight: false,
};

function Legend() {
  return (
    <div className="card">
      <div className="d-flex flex-wrap align-items-center gap-4 p-3">
        <span className="text-muted text-uppercase small fw-semibold">
          Legend
        </span>
        <LegendItem band="red" label="50% or below" />
        <LegendItem band="orange" label="Between 50% – 80%" />
        <LegendItem band="green" label="Above 80%" />
      </div>
    </div>
  );
}

function LegendItem({ band, label }) {
  const colors = BAND_COLORS[band];
  return (
    <span className="d-inline-flex align-items-center gap-2 text-muted small">
      <span
        className="rounded-circle"
        style={{
          width: 10,
          height: 10,
          backgroundColor: colors.dot,
          display: "inline-block",
        }}
      />
      {label}
    </span>
  );
}

LegendItem.propTypes = {
  band: PropTypes.oneOf(["green", "orange", "red"]).isRequired,
  label: PropTypes.string.isRequired,
};
